import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';

import Button from 'components/field/Button';
import OTPInput from 'components/field/OTPInput';
import PasswordInput from 'components/field/PasswordInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';

import axios, { axiosAuth } from 'services/axios';
import { AUTH, MFA } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCopy, faDownload, faPrint } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';

import * as S from './TwoFactorModal.styled';

const TwoFactorRegenerateModal = NiceModal.create(() => {
	const modal = useModal();
	const [value, setValue] = useState([null, null, null, null, null, null]);
	const { showError, showSuccess } = useAlert();
	const [password, setPassword] = useState(null);
	const email = useSelector((state) => state.user.email);
	const [tab, setTab] = useState(1);
	const [loading, setLoading] = useState(false);
	const [useBackupCode, setUseBackupCode] = useState(false);
	const [recoveryCodes, setRecoveryCodes] = useState([]);
	const { t } = useTranslation();

	const login = async () => {
		setLoading(true);
		try {
			const response = await axiosAuth.post(AUTH.LOGIN, {
				email,
				password
			});

			if (response.status === 200) {
				setTab(2);
			} else {
				showError({
					message: 'errorMsg.incorrectPassword'
				});
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: 'errorMsg.incorrectPassword'
			});
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const submit = () => {
		setLoading(true);
		const code = value.join('');
		axios
			.post(MFA.REGENERATE_RECOVERY_CODES, {
				password,
				activationCode: code,
				isBackupCode: useBackupCode
			})
			.then((res) => {
				const { status, data } = res;
				if (status === 200) {
					setRecoveryCodes(data.recoveryCodes);
					setTab(3);
				} else {
					showError(data.code);
				}
			})
			.catch((error) => {
				showError({
					message: error.response?.data?.code
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const closeBackupCodes = () => {
		setValue([null, null, null, null, null, null]);
		setUseBackupCode(false);
	};

	const [isBackedUp, setIsBackedUp] = useState(false);

	const downloadBackupCodes = () => {
		const element = document.createElement('a');
		const file = new Blob([[t('twoFactorModal.backupCodesDownloadTitle'), ...recoveryCodes].join('\n')], {
			type: 'text/plain'
		});
		element.href = URL.createObjectURL(file);
		element.download = 'app.tryoto.com-backup-codes.txt';
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
		setIsBackedUp(true);
		showSuccess({
			message: 'twoFactorModal.backupCodesDownloaded'
		});
	};

	const contentRef = useRef(null);
	const handlePrint = useReactToPrint({ contentRef });

	const printBackupCodes = () => {
		handlePrint();
		setIsBackedUp(true);
		showSuccess({
			message: 'twoFactorModal.backupCodesPrinted'
		});
	};

	const copyBackupCodes = () => {
		navigator.clipboard.writeText(recoveryCodes.join('\n'));
		setIsBackedUp(true);
		showSuccess({
			message: 'twoFactorModal.backupCodesCopied'
		});
	};

	return (
		<Modal maxWidth="small" className="modal-2FA" closeButton={tab !== 3 || isBackedUp}>
			<ModalTitle>
				<Typography variant="p2" fontWeight="500">
					{tab === 3
						? 'twoFactorModal.regeneratedBackupCodesTitle'
						: 'twoFactorModal.regenerateBackupCodesTitle'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				{/*
				 * DON'T REMOVE THIS PRINTCONTAINER. THIS IS USED FOR PRINTING BACKUP CODES!
				 */}
				<S.PrintContainer ref={contentRef}>
					<Typography fontWeight="700">twoFactorModal.printDescription</Typography>
					{recoveryCodes.map((code, index) => (
						<Typography key={index} fontWeight="500">
							{code}
						</Typography>
					))}
				</S.PrintContainer>

				{tab === 1 && (
					<S.TwoFactorBackupCodesModalContainer>
						<FormItem>
							<Typography className="mb-3" fontWeight="500">
								twoFactorModal.regenerateBackupCodesDescription
							</Typography>
						</FormItem>
						{/*
						 * DON'T REMOVE THIS FORM TAG. THIS IS USED CHROME AUTOFILL BUG FIX!
						 */}
						<form method="dialog">
							<FormItem label="twoFactorModal.emailTitle">
								<StringInput value={email} disabled />
							</FormItem>
							<FormItem label="twoFactorModal.passwordTitle" loading={loading}>
								<PasswordInput value={password} setValue={setPassword} />
							</FormItem>
						</form>
					</S.TwoFactorBackupCodesModalContainer>
				)}
				{tab === 2 && (
					<S.TwoFactorBackupCodes2FAContainer>
						<FormItem>
							<Typography fontWeight="500" noWrap>
								{useBackupCode
									? 'twoFactorModal.regenerateBackupCodesWithBackupCodeDescription'
									: 'twoFactorModal.regenerateBackupCodesDescription2'}
							</Typography>
						</FormItem>
						<OTPInput
							value={value}
							setValue={setValue}
							length={6}
							onFilledOnce={submit}
							loading={loading}
						/>
						{!useBackupCode && (
							<S.MFABackupCodeButtonContainer>
								<Typography>login.cantAccessMFA</Typography>
								<S.ButtonLink
									variant="transparent"
									label="login.useABackupCode"
									onClick={() => {
										setValue([null, null, null, null, null, null]);
										setUseBackupCode(true);
									}}
								/>
							</S.MFABackupCodeButtonContainer>
						)}
					</S.TwoFactorBackupCodes2FAContainer>
				)}
				{tab === 3 && (
					<S.TwoFactorBackupCodesContainer>
						<Typography fontWeight="500" noWrap>
							twoFactorModal.regeneratedBackupCodesTabTitle
						</Typography>
						<Alert
							variant="warning"
							showIcon
							header="twoFactorModal.regeneratedBackupCodesTabAlertTitle"
							content="twoFactorModal.regeneratedBackupCodesTabAlertDescription"
						/>
						<S.BackupCodesContainer>
							<S.BackupCodesWrapper>
								{recoveryCodes.map((code, index) => (
									<Typography key={index} fontWeight="500">
										{code}
									</Typography>
								))}
							</S.BackupCodesWrapper>
							<S.BackupCodesButtons>
								<Button
									variant="transparent"
									label="download"
									icon={faDownload}
									size="large"
									onClick={downloadBackupCodes}
								/>
								<S.ButtonsSeparator />
								<Button
									variant="transparent"
									label="print"
									icon={faPrint}
									size="large"
									onClick={printBackupCodes}
								/>
								<S.ButtonsSeparator />
								<Button
									variant="transparent"
									label="copy"
									icon={faCopy}
									size="large"
									onClick={copyBackupCodes}
								/>
							</S.BackupCodesButtons>
						</S.BackupCodesContainer>
					</S.TwoFactorBackupCodesContainer>
				)}
			</ModalContent>
			<ModalActions>
				{tab !== 3 && (
					<Button
						variant="blackOutline"
						label="cancel"
						onClick={modal.remove}
						css={css`
							text-decoration: underline;
							border: none !important;
						`}
					/>
				)}
				{useBackupCode && tab === 2 && (
					<Button
						label="twoFactorModal.previous"
						onClick={closeBackupCodes}
						icon={faChevronLeft}
						variant="outline"
					/>
				)}
				{tab === 1 && <Button label="twoFactorModal.continue" onClick={login} loading={loading} />}
				{tab === 2 && (
					<Button
						label="twoFactorModal.continue"
						onClick={submit}
						disabled={value.includes(null)}
						loading={loading}
					/>
				)}
				{tab === 3 && isBackedUp && <Button label="twoFactorModal.done" onClick={modal.remove} />}
				{tab === 3 && !isBackedUp && (
					<Typography fontWeight="500" fullWidth align="center" className="py-3">
						twoFactorModal.firstlyBackupYourCodes
					</Typography>
				)}
			</ModalActions>
		</Modal>
	);
});

export default TwoFactorRegenerateModal;
